<template>
  <div class="container">
  <ol class="breadcrumb">
    <li><router-link :to="{ name: 'dashboard' }">Dashboard</router-link></li>
    <li><router-link :to="{ name: 'sandbox'}">Malware Analyzer</router-link></li>
    <li class="active">Payload Records</li>
  </ol>

  <bootstrap-modal title="Analysis Details" name="analysisJson">
    <json v-bind:jsonObject="verdict.analysisResult"></json>
  </bootstrap-modal>

  <h4>Payload {{ sha1Checksum }} - {{ size }}</h4>

  <div class="panel-group" id="accordion">

    <bootstrap-panel title="Verdict Details">
      <div class="row">
        <div class="col-sm-6">
          <div v-if="verdict.processingState === PROCESSING_COMPLETED">
            <table class="table table-condensed">
              <tbody>
                <tr>
                  <td>Classification</td>
                  <td>{{ verdict.classification }}</td>
                </tr>
                <tr>
                  <td>Name</td>
                  <td>{{ verdict.virusName }}</td>
                </tr>
                <tr>
                  <td>Date Processed</td>
                  <td>{{ epochDate(verdict.timestamp) }}</td>
                </tr>
                <tr>
                  <td>Analysis Details</td>
                  <td v-if="hasAnalysisResult"><a href="#analysisJson" data-toggle="modal">show</a></td>
                  <td v-else></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else>
            <p>Payload processing {{ verdict.processingState }}</p>
          </div>
        </div>
        <div class="col-sm-6">
          <div v-if="override === null">
            <p>fetching overrides...</p>
          </div>
          <div v-else>
            <p v-if="override === ''">No verdict override configured.</p>
            <p v-else>
              Verdict overridden to <strong>{{ override }}</strong>
            </p>

            <form @submit.prevent="overrideSubmit" class="form-inline">
              <div class="form-group">
                <label>Override Verdict</label>
                <select v-model="override" class="form-control">
                  <option value="" v-bind:selected="override === ''">None</option>
                  <option v-for="verdict in classifications" v-bind:selected="override === verdict" v-bind:value="verdict">
                    {{ verdict }}
                  </option>
                </select>
              </div>
              <button type="submit" class="btn btn-default">Save</button>
            </form>

            <p v-if="updateMessage">{{ updateMessage }}</p>
          </div>
        </div>
      </div>
    </bootstrap-panel>

    <bootstrap-panel title="Record Locations">
      <google-map :coordinates="coordinates" height="400px"></google-map>
    </bootstrap-panel>

    <bootstrap-panel title="Payload Records">
      <table class="table table-condensed">
        <thead>
          <tr>
            <th>Date</th>
            <th>Customer</th>
            <th>Serial</th>
            <th>Service Tag</th>
            <th>Protocol</th>
            <th>Type</th>
            <th>Url</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(record, index) in recordsSorted">
            <td>{{ epochDate(record.timestamp) }}</td>
            <td>{{ record.metadata.licenseCustomer }}</td>
            <td>
              <router-link :to="{ name: 'serial', params: { id: record.metadata.licenseSerial }}">{{ trim(record.metadata.licenseSerial, 8) }}</router-link>
            </td>
            <td>{{ record.metadata.licenseServiceTag }}</td>
            <td>{{ record.metadata.protocol }}</td>
            <td>
              {{ trim(record.metadata.magic, 20) }}
              <bootstrap-popover placement="top" :content="record.metadata.magic">...</bootstrap-popover>
            </td>
            <td>
              {{ trim(record.metadata.url, 20) }}
              <bootstrap-popover placement="top" :content="record.metadata.url">...</bootstrap-popover>
            </td>
          </tr>
        </tbody>
      </table>
    </bootstap-panel>

  </div>
</div>
</template>

<style scoped>
</style>

<script>
import { Promise } from 'es6-promise'
import moment from 'moment'
import _ from 'underscore'
import GoogleMap from './GoogleMap'
import BootstrapPanel from './BootstrapPanel'
import BootstrapPopover from './BootstrapPopover'
import BootstrapModal from './BootstrapModal'
import Json from './Json'

import httpRequest from '../httpRequest'

// must match sandbox verdict model processing state
const SANDBOX_PROCESSING_STATE_COMPLETED = 'COMPLETED'

export default {
  components: {
    GoogleMap,
    BootstrapPanel,
    BootstrapPopover,
    BootstrapModal,
    Json
  },

  data () {
    return {
      sha1Checksum: '',
      size: 0,
      records: [],
      classifications: [],
      override: null,
      updateMessage: '',
      verdict: {},

      PROCESSING_COMPLETED: SANDBOX_PROCESSING_STATE_COMPLETED
    }
  },

  mounted () {
    // console.log('STUFF', this.$route.params)
    this.sha1Checksum = this.$route.params.sha1
    this.size = this.$route.params.size

    this.$apiSandbox.fetchVerdict(this.sha1Checksum, this.size)
    .then((result) => {
        if (result.length > 0) {
          this.verdict = result[0]
        }
        return this.$apiSandbox.fetchRecords(this.sha1Checksum, this.size)
    })
    .then((result) => {
      // console.log('got', result)
      this.records = result
    })

    this.$apiSandbox.fetchVerdictClassifications()
    .then((result) => {
      this.classifications = _.sortBy(result)
    })

    this.$apiSandbox.fetchVerdictOverride(this.sha1Checksum, this.size)
    .then((result) => {
      if (result.data.length === 0) {
        this.override = ''
      } else {
        this.override = result.data[0].classification
      }
    })
  },

  methods: {
    trim (val, length) {
      return val ? val.substring(0, length) : ''
    },
    epochDate (epoch) {
      return moment(epoch, 'x').format('YYYY-MM-DD')
    },
    overrideSubmit () {
      var updatePromise

      if (this.override === '') {
        updatePromise = this.$apiSandbox.deleteVerdictOverride(this.sha1Checksum, this.size)
      } else {
        updatePromise = this.$apiSandbox.updateVerdictOverride(this.sha1Checksum, this.size, this.override)
      }

      updatePromise
      .then((result) => {
        this.updateMessage = 'Successfully updated override.'

        setTimeout(() => {
          this.clearUpdateMessage()
        }, 5000)
      })
      .catch((error) => {
        this.updateMessage = 'Could not update override - an error occurred.'
        console.log('error updating override', error)
      })
    },

    clearUpdateMessage () {
      this.updateMessage = ''
    }
  },

  computed: {
    recordsSorted () {
      return _.sortBy(this.records, 'timestamp')
    },
    ipAddresses () {
      return _.chain(this.records)
      .map((record) => {
        if ('metadata' in record && 'destAddr' in record.metadata) {
          return record.metadata.destAddr
        } else {
          return ''
        }
      })
      .uniq()
      .without('')
      .value()
    },
    hasAnalysisResult () {
      return 'analysisResult' in this.verdict
    }
  },

  asyncComputed: {
    coordinates () {
      var url = 'https://freegeoip.net/csv/'
      var promises = []
      this.ipAddresses.map((ipAddress) => {
        promises.push(httpRequest.get(url + ipAddress).then((result) => {
          var fields = result.split(',')
          return {
            lat: Number(fields[8]),
            long: Number(fields[9])
          }
        }))
      })

      return Promise.all(promises)
    }
  }
}
</script>



// WEBPACK FOOTER //
// SandboxRecord.vue?12fe7ef1